import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { PrimaryCTA } from "@/components/revanche/PrimaryCTA";
import { uploadPhoto, compressImage, previewStart } from "@/lib/photo";
import { EASE } from "@/lib/design";

// Points décoratifs du scan (cascade sur le visage)
const SCAN_POINTS = [
    { top: "34%", left: "36%" },
    { top: "34%", left: "62%" },
    { top: "58%", left: "50%" },
    { top: "74%", left: "50%" },
];

const FacePhoto = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const faceErr = location.state?.erreur;
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const fileRef = useRef(null);
    const [phase, setPhase] = useState("intro"); // intro · camera · scanning
    const [shot, setShot] = useState(null); // { url }

    const stopStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    };
    useEffect(() => () => stopStream(), []);

    const openCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: 720, height: 960 }, audio: false,
            });
            streamRef.current = stream;
            setPhase("camera");
            setTimeout(async () => {
                const v = videoRef.current;
                if (!v) return;
                v.srcObject = stream;
                v.setAttribute("playsinline", "true");
                v.muted = true;
                try { await v.play(); } catch (e) { /* noop */ }
            }, 50);
        } catch (e) {
            // Bascule silencieuse sur le sélecteur de photos
            fileRef.current?.click();
        }
    };

    const startScan = async (blob) => {
        const url = URL.createObjectURL(blob);
        setShot({ url });
        setPhase("scanning");
        stopStream();
        // On téléverse la photo puis on lance l'analyse rapide EN ARRIÈRE-PLAN,
        // pendant que l'utilisateur poursuit (photo du corps, écran des raisons).
        const upload = uploadPhoto(blob, "face")
            .then(() => { previewStart().catch(() => {}); })
            .catch(() => {});
        const wait = new Promise((r) => setTimeout(r, 2000));
        await Promise.all([upload, wait]);
        navigate("/photo-corps");
    };

    const capture = async () => {
        const v = videoRef.current;
        if (!v) return;
        const oc = document.createElement("canvas");
        oc.width = v.videoWidth || 720;
        oc.height = v.videoHeight || 960;
        oc.getContext("2d").drawImage(v, 0, 0, oc.width, oc.height);
        const raw = await new Promise((r) => oc.toBlob(r, "image/jpeg", 0.9));
        const blob = await compressImage(raw, 1024, 0.8); // 1024 px max, JPEG qualité 80
        startScan(blob);
    };

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const blob = await compressImage(f, 1024, 0.8);
        startScan(blob);
    };

    // --- Écran intro ---
    if (phase === "intro") {
        return (
            <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col" data-testid="face-intro-screen">
                <div className="flex-1 min-h-0 flex flex-col justify-center" style={{ paddingLeft: 24, paddingRight: 24 }}>
                    {faceErr && (
                        <div data-testid="face-error-message" className="mb-5 rounded-xl px-4 py-3" style={{ border: "1px solid var(--red)", background: "rgba(225,6,0,0.08)" }}>
                            <p className="font-body" style={{ fontSize: "14px", color: "#FFFFFF" }}>
                                On n'a pas réussi à bien voir ton visage. Reprends une photo bien éclairée, visage dégagé et de face.
                            </p>
                        </div>
                    )}
                    <h1 className="font-body" style={{ fontSize: "24px", fontWeight: 700, lineHeight: 1.15, color: "var(--text)" }}>
                        On passe au scan de ton visage.
                    </h1>
                    <p className="font-body" style={{ marginTop: 8, fontSize: "16px", color: "var(--text-secondary)" }}>
                        Ça prend dix secondes.
                    </p>
                </div>
                <div className="shrink-0 safe-bottom" style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 24 }}>
                    <PrimaryCTA dataTestId="btn-open-camera" onClick={openCamera}>Ouvrir la caméra</PrimaryCTA>
                    <p className="font-body text-center" style={{ marginTop: 12, fontSize: "13px", color: "var(--text-secondary)" }}>
                        Ta photo est chiffrée et supprimable à tout moment.
                    </p>
                </div>
                <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} />
            </div>
        );
    }

    // --- Scan (animation 2s) ---
    if (phase === "scanning") {
        return (
            <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex items-center justify-center" data-testid="face-scan-screen">
                <div className="relative w-full h-full">
                    {shot ? <img src={shot.url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
                    <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.35)" }} />
                    <motion.div
                        className="absolute left-0 right-0"
                        style={{ height: "1px", backgroundColor: "var(--red)" }}
                        initial={{ top: "0%" }}
                        animate={{ top: "100%" }}
                        transition={{ duration: 1.4, ease: "linear" }}
                    />
                    {SCAN_POINTS.map((p, i) => (
                        <motion.span
                            key={i}
                            className="absolute rounded-full"
                            style={{ top: p.top, left: p.left, width: 8, height: 8, backgroundColor: "var(--red)", marginLeft: -4, marginTop: -4 }}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.25, delay: 0.4 + i * 0.3, ease: EASE }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    // --- Caméra live ---
    return (
        <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col" data-testid="face-camera-screen">
            <div className="flex-1 min-h-0 relative">
                <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="relative" style={{ width: "68%", height: "46%" }}>
                        {["top-0 left-0 border-t-2 border-l-2", "top-0 right-0 border-t-2 border-r-2", "bottom-0 left-0 border-b-2 border-l-2", "bottom-0 right-0 border-b-2 border-r-2"].map((c, i) => (
                            <span key={i} className={`absolute w-7 h-7 ${c}`} style={{ borderColor: "var(--red)" }} />
                        ))}
                    </div>
                </div>
                <div className="absolute left-0 right-0 text-center" style={{ bottom: "16%" }}>
                    <span data-testid="face-guidance" className="font-body" style={{ fontSize: "16px", color: "#FFFFFF" }}>
                        Place ton visage dans le cadre
                    </span>
                </div>
            </div>
            <div className="shrink-0 safe-bottom" style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 24, paddingTop: 8 }}>
                <PrimaryCTA dataTestId="btn-capture-face" onClick={capture}>Capturer</PrimaryCTA>
            </div>
        </div>
    );
};

export default FacePhoto;
